<template>
  <a-drawer
    :visible="visible"
    :width="900"
    title="메시지 발송"
    placement="right"
    @close="handleClose"
    class="message-send-drawer"
  >
    <!-- 수신자 필터링 (공통) -->
    <div class="recipients-filter-section">
      <h4 class="section-title">수신자 필터링</h4>
      
      <!-- 블랙리스트 필터 -->
      <div class="filter-controls blacklist-filter">
        <a-form-item label="블랙리스트 필터" style="margin-bottom: 16px;">
          <div class="blacklist-control">
            <a-switch 
              v-model:checked="blacklistFilterEnabled" 
              @change="handleBlacklistFilterToggle"
            >
              <template #checkedChildren>적용</template>
              <template #unCheckedChildren>미적용</template>
            </a-switch>
            <span class="filter-label">블랙리스트 고객 자동 제외</span>
            <a-button 
              v-if="blacklistFilterEnabled && !blacklistChecked" 
              size="small" 
              type="primary" 
              @click="checkBlacklist"
              :loading="checkingBlacklist"
            >
              블랙리스트 검사
            </a-button>
          </div>
        </a-form-item>
        
        <div v-if="blacklistFilterEnabled && blacklistChecked" class="blacklist-result">
          <a-alert
            :message="blacklistInfo.message"
            :type="blacklistInfo.type"
            show-icon
            closable
            @close="clearBlacklistFilter"
          >
            <template #description v-if="blacklistInfo.details">
              <div>대상 총 {{ blacklistInfo.totalCount }}명 중 {{ blacklistInfo.blacklistedCount }}명이 블랙리스트에 포함되어 제외됩니다.</div>
              <div v-if="blacklistInfo.blacklistedCount > 0" class="blacklist-numbers">
                <a-button size="small" type="link" @click="showBlacklistModal = true">
                  제외된 번호 보기
                </a-button>
              </div>
            </template>
          </a-alert>
        </div>
      </div>
      
      <!-- 병원 필터 -->
      <div class="filter-controls">
        <a-form-item label="제외할 병원 입력" style="margin-bottom: 12px;">
          <a-select
            v-model:value="excludedHospitals"
            mode="tags"
            placeholder="제외할 병원명을 입력하세요 (Enter로 추가)"
            style="width: 100%"
            @change="handleHospitalFilterChange"
          >
            <a-select-option v-for="hospital in availableHospitals" :key="hospital" :value="hospital">
              {{ hospital }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <div v-if="excludedHospitals.length > 0" class="excluded-info">
          <a-alert
            :message="`${excludedHospitals.length}개 병원의 고객 ${excludedRecipientsCount}명이 제외되었습니다`"
            type="warning"
            show-icon
            closable
            @close="clearHospitalFilter"
          />
        </div>
      </div>
    </div>

    <!-- 수신자 정보 (공통) -->
    <div class="recipients-section">
      <h4 class="section-title">수신자 정보</h4>
      <div class="recipients-info">
        <a-alert
          :message="`${filteredRecipients.length}명에게 메시지를 발송합니다`"
          :type="filteredRecipients.length === 0 ? 'warning' : 'info'"
          show-icon
        />
        <div class="recipients-list" v-if="filteredRecipients.length <= 5 && filteredRecipients.length > 0">
          <a-tag v-for="recipient in filteredRecipients" :key="recipient.id || recipient._id">
            {{ recipient.name }} ({{ recipient.phone }})
          </a-tag>
        </div>
        <div v-else-if="filteredRecipients.length > 5" class="recipients-summary">
          <a-space>
            <span>{{ filteredRecipients[0].name }} 외 {{ filteredRecipients.length - 1 }}명</span>
            <a-button size="small" @click="showRecipientsModal = true">
              전체 보기
            </a-button>
          </a-space>
        </div>
        <div v-else-if="filteredRecipients.length === 0" class="no-recipients">
          <a-empty description="발송 대상이 없습니다" />
        </div>
      </div>
    </div>

    <!-- 메시지 내용 섹션 (탭으로 구분) -->
    <div class="message-section">
      <h4 class="section-title">메시지 내용</h4>
      
      <!-- 메시지 타입 탭 -->
      <a-tabs v-model:activeKey="activeMessageTab">
        <a-tab-pane key="alimtalk" tab="알림톡">
          <!-- 알림톡 내용 -->
          <a-form
            :model="messageForm"
            :rules="rules"
            ref="formRef"
            layout="vertical"
          >
        <a-form-item label="템플릿 코드" name="templateCode">
          <div style="display: flex; gap: 8px; width: 100%;">
            <a-input 
              v-model:value="messageForm.templateCode" 
              placeholder="템플릿 코드를 입력하세요 (예: PROD_ZEISS_EVENT_WINNER_202501_V1)"
              style="flex: 1"
              :disabled="loadingTemplate"
              @press-enter="fetchTemplate"
            />
            <a-button 
              type="primary" 
              @click="fetchTemplate"
              :loading="loadingTemplate"
              style="flex-shrink: 0"
            >
              조회
            </a-button>
          </div>
          <div v-if="templateInfo" class="template-info">
            <!-- 템플릿 이미지가 있는 경우 표시 -->
            <div v-if="templateInfo.templateImageUrl || templateInfo.templateImageName" class="template-image-preview">
              <img 
                v-if="!imageLoadError"
                :src="getTemplateImageUrl()" 
                :alt="templateInfo.templateImageName || '템플릿 이미지'"
                @error="handleImageError"
                @load="imageLoadError = false"
                style="display: block; margin: 0 auto;"
              />
              <div v-if="imageLoadError" style="color: #999; padding: 20px; text-align: center;">
                이미지를 불러올 수 없습니다
              </div>
            </div>
            
            <a-descriptions :column="1" size="small" bordered class="mt-2">
              <a-descriptions-item label="템플릿 코드">{{ templateInfo.templateCode }}</a-descriptions-item>
              <a-descriptions-item label="템플릿명">{{ templateInfo.templateName }}</a-descriptions-item>
              <a-descriptions-item label="상태">
                <a-space>
                  <a-tag :color="getTemplateStatusColor(templateInfo.templateStatus)">
                    {{ getTemplateStatusText(templateInfo.templateStatus) }}
                  </a-tag>
                  <a-tag v-if="templateInfo.kepStatus" :color="templateInfo.kepStatus === 'O' ? 'green' : 'red'">
                    KEP: {{ templateInfo.kepStatus }}
                  </a-tag>
                </a-space>
              </a-descriptions-item>
              <a-descriptions-item label="메시지 유형">
                {{ getMessageTypeText(templateInfo.templateMessageType) }}
              </a-descriptions-item>
              <a-descriptions-item label="강조 유형" v-if="templateInfo.templateEmphasizeType">
                {{ templateInfo.templateEmphasizeType }}
              </a-descriptions-item>
              <a-descriptions-item label="카테고리 코드" v-if="templateInfo.categoryCode">
                {{ templateInfo.categoryCode }}
              </a-descriptions-item>
              <a-descriptions-item label="버튼" v-if="templateInfo.buttons && templateInfo.buttons.length > 0">
                <div v-for="button in templateInfo.buttons" :key="button.ordering" class="template-button-info">
                  <a-tag color="blue">
                    {{ button.ordering }}. {{ button.name }} ({{ button.linkType }})
                  </a-tag>
                </div>
              </a-descriptions-item>
            </a-descriptions>
          </div>
          <a-alert 
            v-if="templateError" 
            :message="templateError" 
            type="error" 
            class="mt-2"
            closable
            @close="templateError = null"
          />
        </a-form-item>

            <a-form-item label="발송 시간" name="sendTime">
              <a-radio-group v-model:value="messageForm.sendTime">
                <a-radio value="now">즉시 발송</a-radio>
                <a-radio value="scheduled" disabled>예약 발송 (준비중)</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-form>

          <!-- 미리보기 -->
          <div class="preview-section" v-if="templateInfo">
            <h4 class="section-title">메시지 미리보기</h4>
            <div class="preview-container">
              <div class="kakao-preview">
                <div class="kakao-header">
                  <span style="font-size: 13px; font-weight: 600;">서티라이프</span>
                </div>
                <!-- 이미지형 템플릿인 경우 이미지 표시 -->
                <div v-if="templateInfo && (templateInfo.templateImageUrl || templateInfo.templateImageName)" class="kakao-image">
                  <img 
                    v-if="!previewImageError"
                    :src="getTemplateImageUrl()" 
                    :alt="templateInfo.templateImageName || '템플릿 이미지'"
                    @error="handlePreviewImageError"
                    style="width: 100%; display: block;"
                  />
                  <div v-if="previewImageError" style="padding: 40px; text-align: center; color: #999; background: #f5f5f5;">
                    이미지를 불러올 수 없습니다
                  </div>
                </div>
                <div class="kakao-title" v-if="templateInfo.templateName">{{ templateInfo.templateName }}</div>
                <div class="kakao-content" v-if="templateInfo.templateContent">{{ templateInfo.templateContent }}</div>
                <!-- 템플릿에 버튼이 있는 경우 버튼 표시 -->
                <div v-if="templateInfo && templateInfo.buttons && templateInfo.buttons.length > 0" class="kakao-buttons">
                  <div v-for="button in templateInfo.buttons" :key="button.ordering" class="kakao-button">
                    {{ button.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
        
        <a-tab-pane key="friendtalk" tab="친구톡">
          <div class="friendtalk-content">
            <!-- 이미지 유형 -->
            <div>
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                이미지 유형
                <a-switch 
                  v-model:checked="friendtalkForm.isWide"
                  size="small"
                  style="margin-left: 10px;"
                >
                  <template #checkedChildren>와이드</template>
                  <template #unCheckedChildren>일반</template>
                </a-switch>
              </label>
              
              <a-upload
                :before-upload="beforeUploadImage"
                :show-upload-list="false"
                accept="image/*"
              >
                <a-button :loading="uploadingImage">
                  <i class="fa fa-upload" style="margin-right: 8px;"></i>
                  이미지 파일 선택
                </a-button>
              </a-upload>
              <div style="margin-top: 8px; font-size: 12px; color: #666;">
                JPG, PNG 파일만 가능 (최대 10MB)
              </div>
              
              <!-- 업로드된 이미지 표시 -->
              <div v-if="friendtalkForm.imageUrl" class="uploaded-image-preview" style="margin-top: 16px;">
                <div style="position: relative; display: inline-block; width: 100%;">
                  <img 
                    :src="friendtalkForm.imageUrl" 
                    alt="업로드된 이미지"
                    style="max-width: 100%; max-height: 200px; border: 1px solid #d9d9d9; border-radius: 4px;"
                  />
                  <a-button
                    type="text"
                    danger
                    size="small"
                    @click="removeFriendtalkImage"
                    style="position: absolute; top: 5px; right: 5px; background: white;"
                  >
                    <i class="fa fa-times"></i>
                  </a-button>
                </div>
                <div style="margin-top: 8px;">
                  <div style="font-size: 12px; color: #666;">
                    {{ friendtalkForm.isWide ? '와이드 이미지 (800x600)' : '일반 이미지 (500x250)' }}
                  </div>
                  <div style="margin-top: 4px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                    <div style="font-size: 11px; color: #888; margin-bottom: 4px;">카카오 서버 URL:</div>
                    <div style="font-size: 12px; color: #333; word-break: break-all; user-select: text;">
                      {{ friendtalkForm.imageUrl }}
                    </div>
                    <a-button 
                      size="small" 
                      type="link" 
                      @click="copyImageUrl"
                      style="padding: 0; margin-top: 4px;"
                    >
                      <i class="fa fa-copy"></i> URL 복사
                    </a-button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 메시지 작성 영역 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">메시지 내용</label>
              <a-textarea
                v-model:value="friendtalkForm.content"
                placeholder="친구톡 메시지 내용을 작성하세요..."
                :rows="6"
                style="width: 100%"
                :maxlength="1000"
                show-count
              />
            </div>
            
            <!-- 버튼 설정 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">버튼 설정 (선택)</label>
              <a-row :gutter="16">
                <a-col :span="12">
                  <a-input
                    v-model:value="friendtalkForm.buttonName"
                    placeholder="버튼명 입력 (예: 자세히 보기)"
                    :maxlength="14"
                  />
                  <div style="margin-top: 4px; font-size: 12px; color: #666;">
                    최대 14자
                  </div>
                </a-col>
                <a-col :span="12">
                  <a-input
                    v-model:value="friendtalkForm.buttonLink"
                    placeholder="링크 URL (https://...)"
                  />
                  <div style="margin-top: 4px; font-size: 12px; color: #666;">
                    버튼 클릭 시 이동할 URL
                  </div>
                </a-col>
              </a-row>
            </div>
            
            <!-- 미리보기 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">미리보기</label>
              <div class="preview-container">
                <div class="kakao-preview" style="background: #9BBBD4; padding: 0; border-radius: 8px; overflow: hidden;">
                  <!-- 헤더 -->
                  <div style="padding: 12px 16px; background: #9BBBD4; display: flex; align-items: center;">
                    <div style="width: 24px; height: 24px; border-radius: 6px; background: #FEE500; display: flex; align-items: center; justify-content: center; margin-right: 8px;">
                      <span style="font-size: 11px; font-weight: bold;">친구</span>
                    </div>
                    <span style="font-size: 14px; color: #000;">서티라이프 certilife</span>
                  </div>
                  
                  <!-- 메시지 콘텐츠 영역 -->
                  <div style="background: white; margin: 0 8px 8px 8px; border-radius: 8px; overflow: hidden;">
                    <!-- 이미지 -->
                    <div v-if="friendtalkForm.imageUrl" style="width: 100%;">
                      <img 
                        :src="friendtalkForm.imageUrl" 
                        alt="친구톡 이미지"
                        style="width: 100%; display: block;"
                      />
                    </div>
                    
                    <!-- 텍스트 내용 -->
                    <div style="padding: 16px; white-space: pre-wrap; font-size: 14px; line-height: 1.5; color: #000;">{{ friendtalkForm.content || '메시지를 입력하세요...' }}</div>
                    
                    <!-- 버튼 -->
                    <div v-if="friendtalkForm.buttonName" style="padding: 0 16px 16px;">
                      <div style="background: #f7f7f7; border-radius: 6px; padding: 12px; text-align: center; font-size: 14px; color: #000; cursor: pointer;">
                        {{ friendtalkForm.buttonName }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- 하단 시간 표시 -->
                  <div style="padding: 0 16px 12px; font-size: 11px; color: #495057; text-align: right;">
                    오후 3:39
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 하단 액션 버튼 -->
    <template #footer>
      <a-space>
        <a-button @click="handleClose">취소</a-button>
        <a-button 
          type="primary" 
          @click="handleSend"
          :loading="sending"
          :disabled="
            activeMessageTab === 'alimtalk' 
              ? (!templateInfo || filteredRecipients.length === 0)
              : (!friendtalkForm.content || filteredRecipients.length === 0)
          "
          class="yellow-button"
        >
          {{ 
            sending 
              ? '발송 중...' 
              : `${filteredRecipients.length}명에게 ${activeMessageTab === 'friendtalk' ? '친구톡' : '알림톡'} 발송`
          }}
        </a-button>
      </a-space>
    </template>

    <!-- 전체 수신자 목록 모달 -->
    <a-modal
      v-model:visible="showRecipientsModal"
      title="전체 수신자 목록"
      :footer="null"
      width="500px"
    >
      <div v-if="excludedHospitals.length > 0" class="filter-info">
        <a-alert
          :message="`필터링 적용: ${excludedHospitals.join(', ')} 병원 고객 제외`"
          type="warning"
          style="margin-bottom: 16px;"
        />
      </div>
      <a-list
        :data-source="filteredRecipients"
        size="small"
      >
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #title>
                {{ item.name }}
              </template>
              <template #description>
                {{ item.phone }} | {{ item.hospital || '-' }}
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </a-modal>
    <!-- 블랙리스트 번호 목록 모달 -->
    <a-modal
      v-model:visible="showBlacklistModal"
      title="블랙리스트 제외 번호"
      :footer="null"
      width="500px"
    >
      <div class="blacklist-modal-content">
        <a-alert
          :message="`총 ${blacklistInfo.blacklistedCount}개의 번호가 블랙리스트에 포함되어 제외되었습니다.`"
          type="warning"
          style="margin-bottom: 16px;"
        />
        <div class="blacklist-numbers-list">
          <a-tag v-for="phone in blacklistInfo.blacklistedPhoneNumbers" :key="phone" color="red">
            {{ phone }}
          </a-tag>
        </div>
      </div>
    </a-modal>
  </a-drawer>
</template>

<script>
import { ref, reactive, computed, watch, getCurrentInstance, inject } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import eventsApis from '../api';

export default {
  name: 'MessageSendDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recipients: {
      type: Array,
      default: () => []
    },
    eventId: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'send'],
  setup(props, { emit }) {
    const formRef = ref();
    const sending = ref(false);
    const showRecipientsModal = ref(false);
    const loadingTemplate = ref(false);
    const templateInfo = ref(null);
    const templateError = ref(null);
    const imageLoadError = ref(false);
    const previewImageError = ref(false);
    const excludedHospitals = ref([]);
    const excludedRecipientsCount = ref(0);
    const blacklistFilterEnabled = ref(false);
    const blacklistChecked = ref(false);
    const checkingBlacklist = ref(false);
    const activeMessageTab = ref('alimtalk'); // 기본값: 알림톡
    
    // 친구톡 관련 상태
    const friendtalkForm = reactive({
      content: '',
      imageUrl: '',
      isWide: false,
      buttonName: '',
      buttonLink: ''
    });
    const uploadingImage = ref(false);
    
    const blacklistInfo = ref({
      message: '',
      type: 'info',
      details: false,
      totalCount: 0,
      blacklistedCount: 0,
      blacklistedPhoneNumbers: []
    });
    const showBlacklistModal = ref(false);
    
    // API 인스턴스 가져오기
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;
    
    // eventsApis 초기화
    let api = null;
    if (instance && instance.proxy) {
      api = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
    } else {
      console.error('Current instance not available, API initialization failed');
    }
    
    const messageForm = reactive({
      messageType: 'kakao',
      templateCode: '',
      template: '',
      title: '',
      content: '',
      sendTime: 'now',
      scheduledTime: null
    });
    
    const rules = {
      scheduledTime: [
        { required: true, message: '예약 시간을 선택해주세요', trigger: 'change' }
      ]
    };
    
    // 사용 가능한 병원 목록 (수신자로부터 추출)
    const availableHospitals = computed(() => {
      const hospitals = new Set();
      props.recipients.forEach(recipient => {
        if (recipient.hospital && recipient.hospital !== '-') {
          hospitals.add(recipient.hospital);
        }
      });
      return Array.from(hospitals).sort();
    });
    
    // 필터링된 수신자 목록
    const filteredRecipients = computed(() => {
      let recipients = props.recipients;
      
      // 블랙리스트 필터 적용
      if (blacklistFilterEnabled.value && blacklistChecked.value && blacklistInfo.value.blacklistedPhoneNumbers.length > 0) {
        const blacklistedSet = new Set(blacklistInfo.value.blacklistedPhoneNumbers);
        recipients = recipients.filter(recipient => {
          const normalizedPhone = recipient.phone?.replace(/-/g, '');
          return !blacklistedSet.has(normalizedPhone);
        });
      }
      
      // 병원 필터 적용
      if (excludedHospitals.value.length > 0) {
        recipients = recipients.filter(recipient => {
          // 병원 정보가 없거나 '-'인 경우는 포함
          if (!recipient.hospital || recipient.hospital === '-') {
            return true;
          }
          // 제외 병원 목록에 포함된 경우 제외
          return !excludedHospitals.value.includes(recipient.hospital);
        });
      }
      
      return recipients;
    });
    
    // 병원 필터 변경 핸들러
    const handleHospitalFilterChange = () => {
      // 제외된 수신자 수 계산
      excludedRecipientsCount.value = props.recipients.length - filteredRecipients.value.length;
    };
    
    // 병원 필터 초기화
    const clearHospitalFilter = () => {
      excludedHospitals.value = [];
      excludedRecipientsCount.value = 0;
    };
    
    // 블랙리스트 필터 토글
    const handleBlacklistFilterToggle = (checked) => {
      if (!checked) {
        blacklistChecked.value = false;
        blacklistInfo.value = {
          message: '',
          type: 'info',
          details: false,
          totalCount: 0,
          blacklistedCount: 0,
          blacklistedPhoneNumbers: []
        };
      }
    };
    
    // 블랙리스트 검사
    const checkBlacklist = async () => {
      if (!apiInstance) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }
      
      checkingBlacklist.value = true;
      
      try {
        // 전화번호 추출 및 정규화
        const phoneNumbers = props.recipients.map(r => r.phone?.replace(/-/g, '')).filter(Boolean);
        
        if (phoneNumbers.length === 0) {
          message.warning('검사할 전화번호가 없습니다.');
          return;
        }
        
        const response = await apiInstance.post('/events-v2-blacklist-patients/filter-phone-numbers', {
          phoneNumbers
        });
        
        if (response.data?.success && response.data?.data) {
          const data = response.data.data;
          blacklistChecked.value = true;
          
          blacklistInfo.value = {
            message: data.blacklistedCount === 0 
              ? '블랙리스트에 포함된 번호가 없습니다.'
              : `${data.blacklistedCount}명의 블랙리스트 고객이 발견되었습니다.`,
            type: data.blacklistedCount === 0 ? 'success' : 'warning',
            details: true,
            totalCount: data.totalCount,
            blacklistedCount: data.blacklistedCount,
            blacklistedPhoneNumbers: data.blacklistedPhoneNumbers || []
          };
        }
      } catch (error) {
        console.error('블랙리스트 검사 실패:', error);
        message.error('블랙리스트 검사에 실패했습니다.');
      } finally {
        checkingBlacklist.value = false;
      }
    };
    
    // 블랙리스트 필터 초기화
    const clearBlacklistFilter = () => {
      blacklistFilterEnabled.value = false;
      blacklistChecked.value = false;
      blacklistInfo.value = {
        message: '',
        type: 'info',
        details: false,
        totalCount: 0,
        blacklistedCount: 0,
        blacklistedPhoneNumbers: []
      };
    };
    
    // 템플릿 이미지 URL 가져오기
    const getTemplateImageUrl = () => {
      if (!templateInfo.value) return '';
      
      // templateImageUrl이 있으면 그대로 사용
      if (templateInfo.value.templateImageUrl) {
        // URL이 상대 경로인 경우 전체 URL로 변환
        if (templateInfo.value.templateImageUrl.startsWith('/')) {
          return `${window.location.origin}${templateInfo.value.templateImageUrl}`;
        }
        return templateInfo.value.templateImageUrl;
      }
      
      // templateImageName만 있는 경우 기본 경로와 조합
      if (templateInfo.value.templateImageName) {
        // 예시: /api/templates/images/ 또는 적절한 경로
        return `/api/templates/images/${templateInfo.value.templateImageName}`;
      }
      
      return '';
    };
    
    // 템플릿 코드 조회
    const fetchTemplate = async () => {
      if (!messageForm.templateCode) {
        message.warning('템플릿 코드를 입력해주세요.');
        return;
      }
      
      if (!apiInstance) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }
      
      loadingTemplate.value = true;
      templateError.value = null;
      templateInfo.value = null;
      imageLoadError.value = false;
      previewImageError.value = false;
      
      try {
        const response = await apiInstance.get(`/bizmsg/resell/templates/${messageForm.templateCode}`);
        
        // 중첩된 data 구조 처리
        if (response.data && response.data.data && response.data.data.data) {
          templateInfo.value = response.data.data.data;
          
          message.success('템플릿을 불러왔습니다.');
        } else if (response.data && response.data.data) {
          // 다른 응답 구조 처리 (fallback)
          templateInfo.value = response.data.data;
          message.success('템플릿을 불러왔습니다.');
        }
      } catch (error) {
        console.error('템플릿 조회 실패:', error);
        templateError.value = error.response?.data?.message || '템플릿을 조회할 수 없습니다. 템플릿 코드를 확인해주세요.';
      } finally {
        loadingTemplate.value = false;
      }
    };
    
    // 템플릿 상태 색상
    const getTemplateStatusColor = (status) => {
      const colors = {
        'A': 'green',    // 승인
        'R': 'red',      // 반려
        'S': 'orange',   // 검수중
        'C': 'blue'      // 등록
      };
      return colors[status] || 'default';
    };
    
    // 템플릿 상태 텍스트
    const getTemplateStatusText = (status) => {
      const texts = {
        'A': '승인',
        'R': '반려',
        'S': '검수중',
        'C': '등록'
      };
      return texts[status] || status;
    };
    
    // 메시지 유형 텍스트
    const getMessageTypeText = (type) => {
      const types = {
        'BA': '기본형',
        'EX': '부가정보형',
        'AD': '광고추가형',
        'MI': '복합형'
      };
      return types[type] || type;
    };
    
    
    // 파일을 Base64로 변환하는 함수
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };
    
    // 파일 업로드 전 처리
    const beforeUploadImage = async (file) => {
      // 파일 타입 체크
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다');
        return false;
      }
      
      // 파일 크기 체크 (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('이미지 크기는 10MB 이하여야 합니다');
        return false;
      }
      
      uploadingImage.value = true;
      
      try {
        // 친구톡 이미지 파일 직접 업로드 API 사용
        if (!api) {
          console.error('API 인스턴스를 찾을 수 없습니다');
          message.error('API 초기화 오류가 발생했습니다.');
          uploadingImage.value = false;
          return false;
        }
        
        // 친구톡 이미지 파일 업로드 API 호출
        api.uploadFriendtalkImageFile(
          file,
          friendtalkForm.isWide,
          (kakaoUrl) => {
            // 카카오 서버 URL 저장
            friendtalkForm.imageUrl = kakaoUrl;
            message.success('이미지가 카카오 서버에 업로드되었습니다');
            uploadingImage.value = false;
          },
          (error) => {
            console.error('친구톡 이미지 업로드 실패:', error);
            const errorMessage = error.response?.data?.error || error.message || '친구톡 이미지 업로드에 실패했습니다';
            message.error(errorMessage);
            uploadingImage.value = false;
          }
        );
      } catch (error) {
        console.error('파일 처리 실패:', error);
        message.error('파일 처리 중 오류가 발생했습니다');
        uploadingImage.value = false;
      }
      
      return false; // 자동 업로드 방지
    };
    
    // 친구톡 이미지 제거
    const removeFriendtalkImage = () => {
      friendtalkForm.imageUrl = '';
      message.info('이미지가 제거되었습니다');
    };
    
    // 이미지 URL 복사
    const copyImageUrl = () => {
      if (friendtalkForm.imageUrl) {
        navigator.clipboard.writeText(friendtalkForm.imageUrl).then(() => {
          message.success('URL이 클립보드에 복사되었습니다');
        }).catch(() => {
          message.error('URL 복사 실패');
        });
      }
    };
    
    const handleClose = () => {
      // 폼 초기화
      messageForm.messageType = 'kakao';
      messageForm.templateCode = '';
      messageForm.template = '';
      messageForm.title = '';
      messageForm.content = '';
      messageForm.sendTime = 'now';
      messageForm.scheduledTime = null;
      templateInfo.value = null;
      templateError.value = null;
      
      // 친구톡 폼 초기화
      friendtalkForm.content = '';
      friendtalkForm.imageUrl = '';
      friendtalkForm.isWide = false;
      friendtalkForm.buttonName = '';
      friendtalkForm.buttonLink = '';
      excludedHospitals.value = [];
      excludedRecipientsCount.value = 0;
      blacklistFilterEnabled.value = false;
      blacklistChecked.value = false;
      activeMessageTab.value = 'alimtalk'; // 탭 초기화
      blacklistInfo.value = {
        message: '',
        type: 'info',
        details: false,
        totalCount: 0,
        blacklistedCount: 0,
        blacklistedPhoneNumbers: []
      };
      emit('close');
    };
    
    const handleSend = async () => {
      try {
        // 친구톡 탭인 경우
        if (activeMessageTab.value === 'friendtalk') {
          if (filteredRecipients.value.length === 0) {
            message.warning('발송 대상이 없습니다.');
            return;
          }
          
          if (!friendtalkForm.content) {
            message.warning('메시지 내용을 입력해주세요.');
            return;
          }
          
          sending.value = true;
        } else {
          // 알림톡 탭인 경우
          await formRef.value.validate();
          
          if (filteredRecipients.value.length === 0) {
            message.warning('발송 대상이 없습니다.');
            return;
          }
          
          // 템플릿 코드 확인
          if (!messageForm.templateCode) {
            message.warning('템플릿 코드를 입력해주세요.');
            return;
          }
          
          if (!templateInfo.value) {
            message.warning('템플릿을 먼저 조회해주세요.');
            return;
          }
          
          sending.value = true;
        }
        
        try {
          // attendeeIds 추출 (id 또는 _id 사용)
          const attendeeIds = filteredRecipients.value.map(r => r.id || r._id).filter(Boolean);
          
          if (attendeeIds.length === 0) {
            message.error('참여자 ID를 찾을 수 없습니다.');
            sending.value = false;
            return;
          }
          
          // 최대 500명 제한 체크
          if (attendeeIds.length > 500) {
            message.warning('한 번에 최대 500명까지만 발송 가능합니다.');
            sending.value = false;
            return;
          }
          
          // 인증서 수신자 여부 판단 (유입 경로로 판단)
          // deviceId와 referrerId2 모두 없으면 인증서 수신자
          const isCertificateRecipient = filteredRecipients.value.some(r => 
            !r.deviceId && !r.referrerId2
          );
          
          // API 호출 - 친구톡과 알림톡 구분
          let requestBody;
          if (activeMessageTab.value === 'friendtalk') {
            // 친구톡 파라미터 구성
            const friendTalkParam = {
              content: friendtalkForm.content,
              imageUrl: friendtalkForm.imageUrl || null,
              buttonName: friendtalkForm.buttonName || null,
              buttonLink: friendtalkForm.buttonLink || null
            };
            
            requestBody = {
              attendeeIds,
              templateCode: 'FRIEND_TALK',  // 친구톡은 templateCode를 FRIEND_TALK로 고정
              messageType: 'FRIEND_TALK',
              friendTalkParam,
              isCertificateRecipient,
              eventId: props.eventId || undefined
            };
          } else {
            // 알림톡 파라미터 구성
            requestBody = {
              attendeeIds,
              templateCode: messageForm.templateCode,
              isCertificateRecipient,
              eventId: props.eventId || undefined
            };
          }
          
          const response = await apiInstance.post('/events-v2/message/attendees/send-direct', requestBody);
          
          if (response.data?.success) {
            const data = response.data.data;
            const messageTypeName = activeMessageTab.value === 'friendtalk' ? '친구톡' : '알림톡';
            // totalRecipients가 없으면 filteredRecipients.value.length 사용
            const recipientCount = data?.totalRecipients || filteredRecipients.value.length;
            message.success(
              `${recipientCount}명에게 ${messageTypeName} 발송이 큐에 등록되었습니다.`
            );
            
            // 발송 이벤트 emit (성공 시)
            emit('send', {
              recipients: filteredRecipients.value,
              message: messageForm,
              excludedHospitals: excludedHospitals.value,
              response: response.data.data
            });
          } else {
            message.error(response.data?.message || '메시지 발송에 실패했습니다.');
          }
        } catch (error) {
          console.error('메시지 발송 API 호출 실패:', error);
          message.error(
            error.response?.data?.message || 
            '메시지 발송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
          );
        } finally {
          sending.value = false;
        }
        
      } catch (error) {
        console.error('Validation failed:', error);
      }
    };
    
    // 이미지 로드 에러 처리
    const handleImageError = (event) => {
      console.error('템플릿 이미지 로드 실패:', event.target.src);
      imageLoadError.value = true;
    };
    
    // 미리보기 이미지 로드 에러 처리
    const handlePreviewImageError = (event) => {
      console.error('미리보기 이미지 로드 실패:', event.target.src);
      previewImageError.value = true;
    };
    
    return {
      formRef,
      sending,
      showRecipientsModal,
      loadingTemplate,
      templateInfo,
      templateError,
      imageLoadError,
      previewImageError,
      excludedHospitals,
      excludedRecipientsCount,
      blacklistFilterEnabled,
      blacklistChecked,
      checkingBlacklist,
      blacklistInfo,
      showBlacklistModal,
      activeMessageTab,
      friendtalkForm,
      uploadingImage,
      beforeUploadImage,
      removeFriendtalkImage,
      copyImageUrl,
      availableHospitals,
      filteredRecipients,
      getTemplateImageUrl,
      handleHospitalFilterChange,
      clearHospitalFilter,
      handleBlacklistFilterToggle,
      checkBlacklist,
      clearBlacklistFilter,
      messageForm,
      rules,
      fetchTemplate,
      getTemplateStatusColor,
      getTemplateStatusText,
      getMessageTypeText,
      handleClose,
      handleSend,
      handleImageError,
      handlePreviewImageError
    };
  }
};
</script>

<style scoped>
.message-send-drawer :deep(.ant-drawer-body) {
  padding: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
}

.recipients-filter-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.filter-controls {
  margin-top: 12px;
}

.blacklist-filter {
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 16px;
}

.blacklist-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-size: 14px;
  color: #595959;
}

.blacklist-result {
  margin-top: 12px;
}

.blacklist-numbers {
  margin-top: 8px;
}

.blacklist-modal-content .blacklist-numbers-list {
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.excluded-info {
  margin-top: 12px;
}

.recipients-section {
  margin-bottom: 24px;
}

.recipients-info {
  margin-top: 12px;
}

.no-recipients {
  padding: 24px 0;
  text-align: center;
}

.recipients-list {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recipients-summary {
  margin-top: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.message-section {
  margin-bottom: 24px;
}

.preview-section {
  margin-bottom: 24px;
}

.preview-container {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  min-height: 120px;
}

/* SMS 미리보기 스타일 */
.phone-preview {
  max-width: 300px;
  margin: 0 auto;
}

.sms-bubble {
  background: white;
  padding: 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 카카오톡 미리보기 스타일 */
.kakao-preview {
  max-width: 360px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.kakao-header {
  background: #fee500;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}

.kakao-title {
  padding: 12px 12px 8px;
  font-weight: 600;
  font-size: 15px;
  color: #000;
}

.kakao-content {
  padding: 0 12px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
}

/* 카카오톡 이미지 스타일 */
.kakao-image {
  position: relative;
  width: 100%;
}

.kakao-image img {
  width: 100%;
  height: auto;
  display: block;
}

/* 카카오톡 버튼 스타일 */
.kakao-buttons {
  border-top: 1px solid #e5e5e5;
}

.kakao-button {
  padding: 12px;
  text-align: center;
  color: #3b88c3;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
}

.kakao-button:last-child {
  border-bottom: none;
}

.kakao-button:hover {
  background: #f9f9f9;
}


.yellow-button {
  background-color: #FFD814 !important;
  border-color: #FFD814 !important;
}

.yellow-button:hover {
  background-color: #E5C112 !important;
  border-color: #E5C112 !important;
}

/* 템플릿 정보 스타일 */
.template-info {
  margin-top: 12px;
}

.mt-2 {
  margin-top: 8px;
}

.template-info .ant-descriptions {
  background: #f5f5f5;
  border-radius: 4px;
}

.template-info .ant-descriptions-item-label {
  background: #fafafa;
  font-weight: 500;
}


/* 템플릿 이미지 미리보기 */
.template-image-preview {
  margin-bottom: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  text-align: center;
}

.template-image-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

/* 템플릿 버튼 정보 */
.template-button-info {
  margin-bottom: 4px;
}

.template-button-info:last-child {
  margin-bottom: 0;
}

/* 친구톡 더미 콘텐츠 스타일 */
.friendtalk-dummy-content {
  padding: 20px 0;
}

.dummy-section {
  margin-bottom: 24px;
}

.dummy-section .section-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #262626;
}

/* 탭 스타일 */
.message-send-drawer :deep(.ant-tabs) {
  margin-bottom: 0;
}

.message-send-drawer :deep(.ant-tabs-nav) {
  margin-bottom: 24px;
}

.message-send-drawer :deep(.ant-tabs-tab) {
  padding: 12px 20px;
  font-size: 15px;
}

.message-send-drawer :deep(.ant-tabs-content) {
  padding: 0;
}
</style>